import * as B from '@blueprintjs/core';
import {
  ActiveElement,
  Chart,
  ChartEvent,
  PluginOptionsByType,
  ScaleOptionsByType,
  TooltipItem,
} from 'chart.js';
import 'chartjs-adapter-moment';
import autocolorPlugin from 'chartjs-plugin-autocolors';
import chroma from 'chroma-js';
import classnames from 'classnames';
import React from 'react';

import {getGraphTimeRangeWithBuffer, useLineChart} from 'app/components/AnalyzePolygonChart/utils';
import {MFAResponse, TrendLineAggregation} from 'app/components/Dashboard/types';
import {
  LARGE_CHART_HEIGHT,
  MultiFeatureScatterDataPoint,
  SMALL_CHART_HEIGHT,
  calculateFillRange,
  calculateTrendLine,
  formatYValue,
  getDataRange,
  makeMultiFeatureDatasets,
  makeTrendLineChartDatasets,
  updateMultiFeatureDatasetColors,
} from 'app/components/Dashboard/utils/chartUtils';
import {MultifeatureAnalysisConfig} from 'app/src/components/Dashboard/types';
import {DataLayerInfo} from 'app/utils/layers';
import {getLayer} from 'app/utils/layerUtils';

import cs from '../styles/chart.styl';

// If we have more than this many features, we will not show outlines for features by default.
const HIDE_FEATURES_THRESHOLD = 50;

/**
 * Wrapper component for applying controls container styling. Controls are
 * elements displayed in the upper-right corner on hover.
 */
const ControlsContainer: React.FunctionComponent<
  React.PropsWithChildren<{
    toggleFitDataRange: () => void;
    hasDataRangeFit: boolean;
    className?: string;
    disableControls?: boolean;
  }>
> = ({toggleFitDataRange, hasDataRangeFit, className, children, disableControls = false}) => (
  <div className={classnames(cs.controls, className)}>
    {!disableControls && (
      <B.Button
        title="Fit to Y-Axis"
        icon="arrows-vertical"
        active={hasDataRangeFit}
        small
        onClick={() => {
          toggleFitDataRange();
        }}
      />
    )}
    {children}
  </div>
);

/**
 * Component to render a line chart with an arbitrary number of datasets.
 */
const MultiFeatureAnalysisLineChart: React.FunctionComponent<{
  data?: MFAResponse;
  displayFeatureIds: Set<string>;
  hoveredFeatureId: string | null;
  hideLegend?: boolean;
  widgetConfig: MultifeatureAnalysisConfig;
  isExpanded: boolean;
  showAggregateTrend: boolean;
  setDisplayFeatureIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  /**
   * This trendlineAggregation can be different from the widgetConfig.trendLineAggregation
   * because we want different trendlines for specific data. Consider the following cases:
   * - By default, we populate with the average.
   * - When the statAggregation is 'sum', we want to show the sum as the trendline.
   * - Sometimes users may want an average line in the sum statAggregation, we do this
   *   for carbon sometimes so the end result is not a total, stacked chart but an average
   *   of holdings.
   */
  trendLineAggregation: TrendLineAggregation;
}> = ({
  data,
  displayFeatureIds,
  hoveredFeatureId,
  hideLegend = true,
  widgetConfig,
  isExpanded,
  showAggregateTrend,
  setDisplayFeatureIds,
  trendLineAggregation,
}) => {
  const {timeAggregation, layerKey, statAggregation, startDate, endDate, displayOptions} =
    widgetConfig;
  const layerInfo = getLayer(layerKey) as DataLayerInfo;
  // State for tracking whether the y-axis fit is enabled
  const [hasDataRangeFit, setDataRangeFit] = React.useState<boolean>(false);

  // Toggle the y-axis fit state
  const toggleFitDataRange = React.useCallback((): void => {
    setDataRangeFit((p) => !p);
  }, []);

  // Calculate the datasets based on the provided parameters
  const multiFeatureDatasets = React.useMemo(() => {
    if (!data) return null;
    return makeMultiFeatureDatasets(data, timeAggregation);
  }, [data, timeAggregation]);

  const hideFeatures = data && Object.keys(data).length > HIDE_FEATURES_THRESHOLD;

  // Calculate trend line datasets
  const trendLine = React.useMemo(
    () => calculateTrendLine(data, trendLineAggregation),
    [data, trendLineAggregation]
  );

  const fillRange = React.useMemo(() => data && calculateFillRange(data), [data]);

  // Hide fill if there are less than 3 features as it is not meaningful
  const tooFewFeatures = (multiFeatureDatasets?.length || 0) < 3;
  const showFill = !tooFewFeatures && !(statAggregation === 'sum');

  const trendLineDatasets = React.useMemo(
    () =>
      makeTrendLineChartDatasets({
        trendLine,
        layer: layerInfo as DataLayerInfo,
        showFeatureTable: isExpanded,
        timeAggregation,
        percentileRange: showFill ? fillRange : undefined,
        statAggregation: statAggregation,
        hideFill: !showFill,
        hasDataRangeFit,
      }),
    [
      trendLine,
      fillRange,
      layerInfo,
      isExpanded,
      timeAggregation,
      showFill,
      statAggregation,
      hasDataRangeFit,
    ]
  );

  // Combine datasets based on display settings
  const datasets = React.useMemo(() => {
    if (!multiFeatureDatasets || !isExpanded) {
      return trendLineDatasets;
    }

    let chartDatasets = multiFeatureDatasets;
    if (hideFeatures) {
      chartDatasets = multiFeatureDatasets.filter((dataset) => {
        // Get the featureId from the first data point in the dataset
        const firstDataPoint = dataset.data[0] as MultiFeatureScatterDataPoint;
        return (
          firstDataPoint &&
          (displayFeatureIds.has(firstDataPoint.featureId) ||
            (hoveredFeatureId && firstDataPoint.featureId === hoveredFeatureId))
        );
      });
    }

    // Always include trendline datasets when showAggregateTrend is true
    // or when a trendline is being hovered
    return showAggregateTrend || hoveredFeatureId === 'trendline'
      ? [...trendLineDatasets, ...chartDatasets]
      : chartDatasets;
  }, [
    multiFeatureDatasets,
    isExpanded,
    showAggregateTrend,
    hoveredFeatureId,
    trendLineDatasets,
    hideFeatures,
    displayFeatureIds,
  ]);

  /** Calculate x-axis values. */
  const {dates, timeRangeLimits} = React.useMemo(() => {
    const dates = datasets.flatMap((dataset) => dataset.data.map((point) => point.date));
    // If 0 datsets are being displayed, use the start and end date from the widget config
    // This is possible when we have a ton of features so we don't show any transparent datasets
    // on the chart, just the selected features.
    const fallbackTimeRangeLimits = [new Date(startDate), new Date(endDate)];
    const timeRangeLimits = getGraphTimeRangeWithBuffer(
      dates.length > 0 ? dates : fallbackTimeRangeLimits
    );
    return {dates, timeRangeLimits};
  }, [datasets, startDate, endDate]);

  const legendPluginOptions = React.useMemo<PluginOptionsByType<'line'>['legend']>(
    () =>
      ({
        display: !hideLegend,
        labels: {
          generateLabels: (chart) => {
            return [...Chart.defaults.plugins.legend.labels.generateLabels(chart)];
          },
        },
      }) as PluginOptionsByType<'line'>['legend'],
    [hideLegend]
  );

  /** Allow chart to be optionally panned and zoomed. */
  const zoomPluginOptions = React.useMemo<PluginOptionsByType<'line'>['zoom']>(
    () => ({
      limits: {
        x: {
          min: timeRangeLimits[0].getTime(),
          max: timeRangeLimits[1].getTime(),
          minRange: 15 * 24 * 60 * 60 * 1000,
        },
      },
      pan: {
        mode: 'x',
        enabled: true,
      },
      zoom: {
        mode: 'x',
        pinch: {
          enabled: true,
        },
        wheel: {
          enabled: true,
        },
      },
    }),
    [timeRangeLimits]
  );

  /** Configure time scale (i.e., x-axis) settings. */
  const xScaleOptions = React.useMemo<ScaleOptionsByType<'time'>>(() => {
    return {
      type: 'time',
      min: timeRangeLimits[0],
      max: timeRangeLimits[1],
      time: {
        unit: timeAggregation === 'yearly' ? 'year' : 'month',
        displayFormats: {
          month: 'MMM YYYY',
          year: 'YYYY',
        },
        tooltipFormat: timeAggregation === 'yearly' ? 'YYYY' : 'MMM YYYY',
      },
      ticks: {
        autoSkip: true,
        autoSkipPadding: 10,
        minRotation: 0,
        maxRotation: 0,
      },
      grid: {
        borderDash: [1, 1],
      },
    } as unknown as ScaleOptionsByType<'time'>;
  }, [timeRangeLimits, timeAggregation]);

  /** Configure linear scale (i.e., y-axis) settings. */
  const yScaleOptions = React.useMemo<ScaleOptionsByType<'linear'>>(() => {
    const {dataRange} = displayOptions || {};
    const [min, max] = dataRange
      ? dataRange
      : getDataRange(widgetConfig, datasets, hasDataRangeFit);

    return {
      type: 'linear',
      min,
      max,
      ticks: {
        precision: 1,
        maxTicksLimit: 2,
        callback: function (value, _index, _ticks) {
          return formatYValue(value as number, statAggregation, layerInfo);
        },
      },

      border: {
        display: false,
        dash: [1, 1],
      },
    } as ScaleOptionsByType<'linear'>;
  }, [displayOptions, widgetConfig, datasets, hasDataRangeFit, statAggregation, layerInfo]);

  /** Optionally display a tooltip on point hover. */
  const tooltipPluginOptions = React.useMemo<PluginOptionsByType<'line'>['tooltip']>(
    () =>
      ({
        filter: (tooltipItem: TooltipItem<'line'>) => {
          const dataPoint = tooltipItem.raw as MultiFeatureScatterDataPoint;
          // Don't show tooltips for upperBound and lowerBound datasets
          return !(dataPoint && ['upperBound', 'lowerBound'].includes(dataPoint.featureId));
        },
        enabled: true,
        callbacks: {
          label: (tooltipItem) => {
            const featureName = (tooltipItem.raw as MultiFeatureScatterDataPoint).featureName;
            return `${featureName}: ${formatYValue(tooltipItem.parsed.y, statAggregation, layerInfo)}`;
          },
          ...(isExpanded && {
            labelColor: (tooltipItem) => {
              const backgroundColor = tooltipItem.dataset.backgroundColor;
              const borderColor = tooltipItem.dataset.borderColor;
              return {
                backgroundColor:
                  typeof backgroundColor === 'string'
                    ? chroma(backgroundColor).alpha(1).hex()
                    : backgroundColor,
                borderColor:
                  typeof borderColor === 'string'
                    ? chroma(borderColor).alpha(1).hex()
                    : borderColor,
              };
            },
          }),
        },
      }) as PluginOptionsByType<'line'>['tooltip'] & {
        // Declaring filter separately to avoid type error due to missing type in Plugin typings
        filter: (tooltipItem: TooltipItem<'line'>) => boolean;
      },
    [layerInfo, statAggregation, isExpanded]
  );

  // TODO: set to false, we really don't need this plugin at all. Keeping in for now in case we need it.
  const titlePluginOptions = React.useMemo<Partial<PluginOptionsByType<'line'>['title']>>(
    () => ({
      display: false,
      text: layerInfo.display,
      font: {weight: 'normal'} as PluginOptionsByType<'line'>['title']['font'],
    }),
    [layerInfo.display]
  );

  // If we click on a data point, we want to add/remove it from the displayed features.
  const onClick = React.useCallback(
    (_event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (elements.length > 0) {
        const element = elements[0];
        const dataset = chart.data.datasets[element.datasetIndex];
        const dataPoint = dataset.data[element.index] as MultiFeatureScatterDataPoint;

        if (!displayFeatureIds.has(dataPoint.featureId)) {
          setDisplayFeatureIds((prevDisplayFeatureIds) => {
            const next = new Set(prevDisplayFeatureIds);
            next.add(dataPoint.featureId);
            return next;
          });
        } else {
          setDisplayFeatureIds((prevDisplayFeatureIds) => {
            const next = new Set(prevDisplayFeatureIds);
            next.delete(dataPoint.featureId);
            return next;
          });
        }
      }
    },
    [displayFeatureIds, setDisplayFeatureIds]
  );

  // Get chart instance from useLineChart
  const {canvasRefCallback, chart} = useLineChart({
    datasets,
    labels: dates,
    annotationPluginOptions: {
      annotations: [],
    },
    tooltipPluginOptions,
    legendPluginOptions,
    zoomPluginOptions,
    titlePluginOptions,
    xScaleOptions,
    yScaleOptions,
    plugins: [autocolorPlugin],
    isExpanded,
    onClick,
  });

  // Update dataset colors when hoveredFeatureId or displayFeatureIds change
  React.useEffect(() => {
    if (chart) {
      if (isExpanded) {
        updateMultiFeatureDatasetColors(chart, hoveredFeatureId, displayFeatureIds);
        chart.update();
      }
    }
  }, [chart, hoveredFeatureId, displayFeatureIds, isExpanded]);

  // Update chart when hasDataRangeFit changes
  React.useEffect(() => {
    if (chart) {
      chart.update();
    }
  }, [chart, hasDataRangeFit]);

  return (
    <React.Fragment>
      <div
        style={{height: isExpanded ? LARGE_CHART_HEIGHT : SMALL_CHART_HEIGHT}}
        className={cs.chartWithHiddenControls}
      >
        <canvas ref={canvasRefCallback} />
        <ControlsContainer
          toggleFitDataRange={toggleFitDataRange}
          hasDataRangeFit={hasDataRangeFit}
          disableControls={statAggregation === 'sum'}
        />
      </div>
    </React.Fragment>
  );
};

export default MultiFeatureAnalysisLineChart;
